import type { ResponseMerchFlowDetails } from "@CommonApi/merchandise";
import type { ResponseGetMerchflowsCategories } from "@CommonApi/merchflows/categories";
import {
  type FlowReviewSortableColumn,
  type ParamsGetMerchflowsReviews,
  type ResponseGetMerchflowsReviews,
  RouteGetMerchflowsReviews,
} from "@CommonApi/merchflows/reviews";
import { ReviewStatus } from "@CommonTypes/merchflow/ReviewStatus";
import { routeToUrl } from "@CommonUtils/routes";
import { hasPermission, PermissionCode } from "@CommonUtils/RolesPermissions";
import type {
  CellContextMenuEvent,
  ColDef,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  ValueFormatterParams,
} from "ag-grid-community";
import type React from "react";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useApi } from "src/api";
import { useMerchflowsApi } from "src/api/merchflows";
import { Page, Tooltip } from "src/components";
import { AgGridTable } from "src/components/AgGridTable";
import { useAgGridLayoutPersistence } from "src/components/AgGridTable/store/layoutPersistence";
import { BackendDateRangeFilter } from "src/components/AgGridTable/filters/BackendDateRangeFilter";
import { BackendInputFilter } from "src/components/AgGridTable/filters/BackendInputFilter";
import { MultiSelectFilter } from "src/components/AgGridTable/filters/MultiSelectFilter";
import { BackendSortHeader } from "src/components/AgGridTable/headers/BackendSortHeader";
import { useLoaders } from "src/components/InlineLoader";
import { useMenu } from "src/components/Menu";
import { MerchflowHeader } from "src/components/merchflows/MerchflowHeader/MerchflowHeader";
import { useModals } from "src/components/Modals/store/hooks";
import { useToasts } from "src/components/Toasts";
import { useTranslation } from "src/i18n";
import { useUser } from "src/atoms/user";
import { Button, Color, Flex, Icon, Pagination, Scroller, Text } from "src/elements";
import { useMerchflowNotesModal } from "src/modals/MerchflowNotes/store/hooks";
import { useMerchflowReviewModal } from "src/modals/MerchflowReview/store/hooks";
import { useMerchflowReviewUpdateModal } from "src/modals/MerchflowReviewUpdate/store/hooks";
import { useViolationsReportModal } from "src/modals/ViolationsReport/store/hooks";
import { useDuplicateMerchflowModal } from "src/modals/ModalDuplicateMerchflow/store/hooks";
import {
  gridFilterModelToQueryParams,
  queryParamsToGridFilterModel,
} from "src/pages/Merchflows/store/agGridModelMapper";
import {
  buildMerchflowsFilterSearchParams,
  filterMultiSelectValues,
  getAvailableSubcategoryOptions,
  type MerchflowsSearchParamsInput,
  parseCsvSearchParam,
  serializeCsvSearchParam,
} from "src/pages/Merchflows/store/filters";
import {
  buildMerchflowInlineIndicators,
  buildMerchflowRowMenuOptions,
  type MerchflowInlineIndicator,
} from "src/pages/Merchflows/store/rowActions";
import { getNextMerchflowsSort } from "src/pages/Merchflows/store/sort";
import { dateIsoStringToLocaleString, isMerchflowActionHidden, pages } from "src/utils";
import { useHoverClickable } from "src/utils/hoverClickable";
import { useNavigation } from "src/utils/navigation";
import { useSearchParams } from "src/utils/useSearchParams";

const getMerchflowStatusLabel = (status: ResponseMerchFlowDetails["status"]) => {
  switch (status) {
    case "DRAFT":
      return "Draft";
    case "S0_GENERATED":
      return "CDT0 Mudmap generated";
    case "S1_GENERATED":
      return "CDT1 Mudmap generated";
    case "S2_GENERATED":
      return "CDT2 Mudmap generated";
    case "BASE_POG_GENERATED":
      return "Base POG generated";
    case "STORE_POG_GENERATED":
      return "Store POG generated";
    case "STORE_POG_EXPORTED":
      return "Store POG exported";
  }
};

const statusFilterOptions = [
  ReviewStatus.DRAFT,
  ReviewStatus.S0_GENERATED,
  ReviewStatus.S1_GENERATED,
  ReviewStatus.S2_GENERATED,
  ReviewStatus.BASE_POG_GENERATED,
  ReviewStatus.STORE_POG_GENERATED,
  ReviewStatus.STORE_POG_EXPORTED,
].map((status) => ({
  value: status,
  label: getMerchflowStatusLabel(status) || status,
}));

const defaultColDef: ColDef<ResponseMerchFlowDetails> = {
  sortable: false,
  filter: false,
  resizable: true,
  cellStyle: {
    display: "flex",
    alignItems: "center",
  },
};

const idFilterParams = {
  inputType: "number" as const,
  placeholder: "Filter ID",
};

const nameFilterParams = {
  inputType: "text" as const,
  placeholder: "Search name",
};

const merchflowsGridLayoutStorageKey = "merchflows-table-layout:v2";

const getMerchflowIndicatorColor = (indicator: MerchflowInlineIndicator) =>
  indicator.kind === "violation" ? Color.red : Color.yellow;

const normalizeSearchParams = (params: MerchflowsSearchParamsInput) => {
  const cleanSearchParams: { [key: string]: string } = {};

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || (key === "page" && (value === 1 || value === "1"))) {
      continue;
    } else if (typeof value === "boolean" && value === true) {
      cleanSearchParams[key] = "true";
    } else if (typeof value === "number") {
      cleanSearchParams[key] = String(value);
    } else if (typeof value === "string" && value !== "") {
      cleanSearchParams[key] = value;
    }
  }

  return cleanSearchParams;
};

const areSearchParamsEqual = (
  currentParams: MerchflowsSearchParamsInput,
  nextParams: MerchflowsSearchParamsInput,
) => {
  const current = normalizeSearchParams(currentParams);
  const next = normalizeSearchParams(nextParams);

  const currentKeys = Object.keys(current);
  const nextKeys = Object.keys(next);

  if (currentKeys.length !== nextKeys.length) {
    return false;
  }

  return currentKeys.every((key) => current[key] === next[key]);
};

const toFiniteNumberSearchParam = (value: string | undefined) => {
  if (value === undefined || value === "") {
    return undefined;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : undefined;
};

export const PageMerchflows = () => {
  const { toast } = useToasts();
  const { t: tCommon } = useTranslation("common");
  const { t: tDelivery } = useTranslation("planogramDelivery");
  const { openMenu } = useMenu();
  const { getMerchflowsReviewsApi } = useApi();
  const { getMerchflowsCategoriesApi, deleteMerchflowApi } = useMerchflowsApi();
  const { navigate } = useNavigation();
  const { user } = useUser();
  const { openConfirmModal } = useModals();
  const canDelete = hasPermission(user.roleCode, PermissionCode.MERCHFLOW_DELETE);
  const canCreate = hasPermission(user.roleCode, PermissionCode.MERCHFLOW_CREATE);
  const { openMerchflowReviewModal } = useMerchflowReviewModal();
  const { hasLoader } = useLoaders();
  const { openViolationsReportModal } = useViolationsReportModal();
  const { openMerchflowReviewUpdateModal } = useMerchflowReviewUpdateModal();
  const { openDuplicateMerchflowModal } = useDuplicateMerchflowModal();
  const { searchParams, setSearchParams } = useSearchParams();
  const { persistGridLayout, restoreGridLayout } =
    useAgGridLayoutPersistence<ResponseMerchFlowDetails>(merchflowsGridLayoutStorageKey);
  const hideMerchflowActions = isMerchflowActionHidden();

  const getMerchflowsReviewsApiRef = useRef(getMerchflowsReviewsApi);
  const getMerchflowsCategoriesApiRef = useRef(getMerchflowsCategoriesApi);
  const gridApiRef = useRef<GridApi<ResponseMerchFlowDetails> | null>(null);
  const isProgrammaticGridSyncRef = useRef(false);
  const gridSyncTimeoutRef = useRef<number | undefined>(undefined);

  const [availableCategories, setAvailableCategories] =
    useState<ResponseGetMerchflowsCategories | null>(null);
  const [data, setData] = useState<ResponseGetMerchflowsReviews | null | undefined>(undefined);
  const canValidateSubcategories = availableCategories !== null;
  const availableCategoriesMap = availableCategories || {};

  const page = Number(searchParams["page"] || "1") || 1;
  const id = toFiniteNumberSearchParam(searchParams["id"]);
  const name = searchParams["name"] || undefined;
  const status = useMemo(
    () => parseCsvSearchParam<ReviewStatus>(searchParams["status"]),
    [searchParams["status"]],
  );
  const category = useMemo(
    () => parseCsvSearchParam(searchParams["category"]),
    [searchParams["category"]],
  );
  const rawSubcategory = useMemo(
    () => parseCsvSearchParam(searchParams["subcategory"]),
    [searchParams["subcategory"]],
  );
  const goLiveDateFrom = searchParams["goLiveDateFrom"] || undefined;
  const goLiveDateTo = searchParams["goLiveDateTo"] || undefined;
  const dateCreatedFrom = searchParams["dateCreatedFrom"] || undefined;
  const dateCreatedTo = searchParams["dateCreatedTo"] || undefined;
  const dateModifiedFrom = searchParams["dateModifiedFrom"] || undefined;
  const dateModifiedTo = searchParams["dateModifiedTo"] || undefined;
  const deactivationDateFrom = searchParams["deactivationDateFrom"] || undefined;
  const deactivationDateTo = searchParams["deactivationDateTo"] || undefined;
  const sortBy = searchParams["sortBy"] as ParamsGetMerchflowsReviews["sortBy"] | undefined;
  const sortDirection = searchParams["sortDirection"] as
    | ParamsGetMerchflowsReviews["sortDirection"]
    | undefined;

  const isLoadingFlows = data === undefined || hasLoader(RouteGetMerchflowsReviews);

  useEffect(() => {
    getMerchflowsReviewsApiRef.current = getMerchflowsReviewsApi;
  }, [getMerchflowsReviewsApi]);

  useEffect(() => {
    getMerchflowsCategoriesApiRef.current = getMerchflowsCategoriesApi;
  }, [getMerchflowsCategoriesApi]);

  useEffect(
    () => () => {
      if (gridSyncTimeoutRef.current !== undefined) {
        window.clearTimeout(gridSyncTimeoutRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    let isCancelled = false;

    const loadCategories = async () => {
      const response = await getMerchflowsCategoriesApiRef.current();

      if (isCancelled) {
        return;
      }

      if (response !== null) {
        setAvailableCategories(response);
      }
    };

    void loadCategories();

    return () => {
      isCancelled = true;
    };
  }, []);

  const categoryOptions = useMemo(
    () => Object.keys(availableCategoriesMap).sort((a, b) => a.localeCompare(b)),
    [availableCategoriesMap],
  );

  const subcategoryOptions = useMemo(
    () => getAvailableSubcategoryOptions(availableCategoriesMap, category),
    [availableCategoriesMap, category],
  );

  const subcategory = useMemo(
    () =>
      canValidateSubcategories
        ? filterMultiSelectValues(rawSubcategory, subcategoryOptions)
        : rawSubcategory,
    [canValidateSubcategories, rawSubcategory, subcategoryOptions],
  );

  const loadFlows = useCallback(async (queryParams: ParamsGetMerchflowsReviews) => {
    setData(await getMerchflowsReviewsApiRef.current(queryParams));
  }, []);

  const currentQueryParams = useMemo<ParamsGetMerchflowsReviews>(
    () => ({
      page,
      size: 100,
      id,
      name,
      status,
      category,
      subcategory,
      goLiveDateFrom,
      goLiveDateTo,
      dateCreatedFrom,
      dateCreatedTo,
      dateModifiedFrom,
      dateModifiedTo,
      deactivationDateFrom,
      deactivationDateTo,
      sortBy,
      sortDirection,
    }),
    [
      page,
      id,
      name,
      status,
      category,
      subcategory,
      goLiveDateFrom,
      goLiveDateTo,
      dateCreatedFrom,
      dateCreatedTo,
      dateModifiedFrom,
      dateModifiedTo,
      deactivationDateFrom,
      deactivationDateTo,
      sortBy,
      sortDirection,
    ],
  );

  useEffect(() => {
    void loadFlows({
      page,
      size: 100,
      id,
      name,
      status,
      category,
      subcategory,
      goLiveDateFrom,
      goLiveDateTo,
      dateCreatedFrom,
      dateCreatedTo,
      dateModifiedFrom,
      dateModifiedTo,
      deactivationDateFrom,
      deactivationDateTo,
      sortBy,
      sortDirection,
    });
  }, [
    loadFlows,
    page,
    id,
    name,
    status,
    category,
    subcategory,
    goLiveDateFrom,
    goLiveDateTo,
    dateCreatedFrom,
    dateCreatedTo,
    dateModifiedFrom,
    dateModifiedTo,
    deactivationDateFrom,
    deactivationDateTo,
    sortBy,
    sortDirection,
  ]);

  const applySearchParams = useCallback(
    (nextSearchParams: MerchflowsSearchParamsInput) => {
      if (areSearchParamsEqual(searchParams, nextSearchParams)) {
        return;
      }

      setSearchParams(nextSearchParams);
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    if (!canValidateSubcategories) {
      return;
    }

    const serializedRawSubcategory = serializeCsvSearchParam(rawSubcategory);
    const serializedSubcategory = serializeCsvSearchParam(subcategory);

    if (serializedRawSubcategory === serializedSubcategory) {
      return;
    }

    applySearchParams({
      ...searchParams,
      subcategory: serializedSubcategory,
    });
  }, [applySearchParams, canValidateSubcategories, rawSubcategory, searchParams, subcategory]);

  const handleSortRequested = useCallback(
    (sortKey: FlowReviewSortableColumn) => {
      const nextSort = getNextMerchflowsSort(
        {
          sortBy,
          sortDirection,
        },
        sortKey,
      );

      applySearchParams({
        ...searchParams,
        page: 1,
        sortBy: nextSort.sortBy,
        sortDirection: nextSort.sortDirection,
      });
    },
    [applySearchParams, searchParams, sortBy, sortDirection],
  );

  const getSortableHeaderParams = useCallback(
    (sortKey: FlowReviewSortableColumn) => ({
      innerHeaderComponent: BackendSortHeader,
      innerHeaderComponentParams: {
        sortKey,
        activeSortBy: sortBy,
        activeSortDirection: sortDirection,
        onSortChange: handleSortRequested,
      },
    }),
    [handleSortRequested, sortBy, sortDirection],
  );

  const syncGridFiltersFromQueryParams = useCallback(
    (api: GridApi<ResponseMerchFlowDetails>) => {
      isProgrammaticGridSyncRef.current = true;

      const nextFilterModel = queryParamsToGridFilterModel({
        id,
        name,
        status,
        category,
        subcategory,
        goLiveDateFrom,
        goLiveDateTo,
        dateCreatedFrom,
        dateCreatedTo,
        dateModifiedFrom,
        dateModifiedTo,
        deactivationDateFrom,
        deactivationDateTo,
      });
      api.setFilterModel(nextFilterModel);

      if (gridSyncTimeoutRef.current !== undefined) {
        window.clearTimeout(gridSyncTimeoutRef.current);
      }

      gridSyncTimeoutRef.current = window.setTimeout(() => {
        isProgrammaticGridSyncRef.current = false;
      }, 0);
    },
    [
      id,
      name,
      status,
      category,
      subcategory,
      goLiveDateFrom,
      goLiveDateTo,
      dateCreatedFrom,
      dateCreatedTo,
      dateModifiedFrom,
      dateModifiedTo,
      deactivationDateFrom,
      deactivationDateTo,
    ],
  );

  const onGridReady = useCallback(
    (event: GridReadyEvent<ResponseMerchFlowDetails>) => {
      gridApiRef.current = event.api;
      restoreGridLayout(event.api);
      syncGridFiltersFromQueryParams(event.api);
    },
    [restoreGridLayout, syncGridFiltersFromQueryParams],
  );

  useEffect(() => {
    const api = gridApiRef.current;
    if (!api) {
      return;
    }

    syncGridFiltersFromQueryParams(api);
  }, [syncGridFiltersFromQueryParams, categoryOptions, subcategoryOptions]);

  const handleFilterChanged = useCallback(() => {
    if (isProgrammaticGridSyncRef.current) {
      return;
    }

    const api = gridApiRef.current;
    if (!api) {
      return;
    }

    const filterQueryParams = gridFilterModelToQueryParams(api.getFilterModel());

    applySearchParams(
      buildMerchflowsFilterSearchParams({
        currentSearchParams: searchParams,
        filterQueryParams,
        availableCategories: availableCategoriesMap,
        canValidateSubcategories,
      }),
    );
  }, [applySearchParams, availableCategoriesMap, canValidateSubcategories, searchParams]);

  const viewTemplates = useCallback(
    (merchflowId: number) => {
      navigate(routeToUrl(pages.merchflowTemplates, { merchflowId }));
    },
    [navigate],
  );

  const viewStores = useCallback(
    (merchflowId: number) => {
      navigate(routeToUrl(pages.merchflows_merchflowId_StoresAll, { merchflowId }));
    },
    [navigate],
  );

  const showContinueWarningToast = useCallback(
    (message: string) => {
      toast({ title: "Can't continue merchflow", message, type: "WARNING" });
    },
    [toast],
  );

  const continueMerchflow = useCallback(
    (merchflow: ResponseMerchFlowDetails) => {
      switch (merchflow.status) {
        case ReviewStatus.DRAFT:
          showContinueWarningToast("Not implemented");
          break;
        case ReviewStatus.S0_GENERATED:
          if (merchflow.latest_s0_user_trigger_id) {
            navigate(
              routeToUrl(pages.merchflows_merchflowId_S0_s0UserTriggerId_, {
                merchflowId: merchflow.id,
                s0UserTriggerId: merchflow.latest_s0_user_trigger_id,
              }),
            );
          } else {
            showContinueWarningToast("CDT0 mudmap not found");
          }
          break;
        case ReviewStatus.S1_GENERATED:
        case ReviewStatus.S2_GENERATED:
          if (merchflow.latest_s1_user_trigger_id) {
            navigate(
              routeToUrl(pages.merchflows_merchflowId_S1_s1UserTriggerId_Templates, {
                merchflowId: merchflow.id,
                s1UserTriggerId: merchflow.latest_s1_user_trigger_id,
              }),
            );
          } else {
            showContinueWarningToast("CDT1 mudmap not found");
          }
          break;
        case ReviewStatus.BASE_POG_GENERATED:
          if (merchflow.latest_base_pog_id) {
            navigate(
              routeToUrl(pages.merchflows_merchflowId_BasePog_basePogId_, {
                merchflowId: merchflow.id,
                basePogId: merchflow.latest_base_pog_id,
              }),
            );
          } else {
            showContinueWarningToast("Base pog id not found");
          }
          break;
        case ReviewStatus.STORE_POG_GENERATED:
        case ReviewStatus.STORE_POG_EXPORTED:
          viewStores(merchflow.id);
          break;
      }
    },
    [navigate, showContinueWarningToast, viewStores],
  );

  const openMerchflowByDefault = useCallback(
    (merchflow: ResponseMerchFlowDetails) => {
      if (hideMerchflowActions || merchflow.status === ReviewStatus.DRAFT) {
        viewStores(merchflow.id);
        return;
      }

      continueMerchflow(merchflow);
    },
    [continueMerchflow, hideMerchflowActions, viewStores],
  );

  const handleUpdateFlowReview = useCallback(
    (review: ResponseMerchFlowDetails) => {
      openMerchflowReviewUpdateModal({ merchflowId: review.id });
    },
    [openMerchflowReviewUpdateModal],
  );

  const handleDeleteMerchflow = useCallback(
    (merchflow: ResponseMerchFlowDetails) => {
      const finishDelete = () => {
        toast({ title: tDelivery("deleteMerchflow.deletedToast", { name: merchflow.name }) });
        const isLastItemOnPage = data?.data.length === 1;
        const reloadPage = isLastItemOnPage && page > 1 ? page - 1 : page;
        void loadFlows({
          ...currentQueryParams,
          page: reloadPage,
        });
      };

      openConfirmModal({
        title: `Delete "${merchflow.name}"?`,
        description:
          "This will permanently delete the merchflow and all associated data including POGs, exports, templates and Azure blob files. This action cannot be undone.",
        icon: { name: "alert", color: Color.red },
        buttons: [
          { id: "cancel", label: "Cancel", color: "primary", variant: "inverted" },
          {
            id: "delete",
            label: "Delete",
            color: "red",
            onClick: async () => {
              const result = await deleteMerchflowApi(merchflow.id);

              if (result?.status === "deleted") {
                finishDelete();
                return;
              }

              if (result?.status === "confirmation_required") {
                const { deliveryCount } = result;
                openConfirmModal({
                  title: tDelivery("deleteMerchflow.confirmTitle", { name: merchflow.name }),
                  description: tDelivery(
                    deliveryCount === 1
                      ? "deleteMerchflow.confirmDescriptionSingular"
                      : "deleteMerchflow.confirmDescriptionPlural",
                    { count: deliveryCount },
                  ),
                  icon: { name: "alert", color: Color.red },
                  buttons: [
                    {
                      id: "cancel",
                      label: tCommon("cancel"),
                      color: "primary",
                      variant: "inverted",
                    },
                    {
                      id: "delete",
                      label: tDelivery("deleteMerchflow.deleteAnyway"),
                      color: "red",
                      onClick: async () => {
                        const confirmedResult = await deleteMerchflowApi(merchflow.id, true);
                        if (confirmedResult?.status === "deleted") {
                          finishDelete();
                        }
                      },
                    },
                  ],
                });
                return false;
              }
            },
          },
        ],
      });
    },
    [
      currentQueryParams,
      data?.data.length,
      deleteMerchflowApi,
      loadFlows,
      openConfirmModal,
      page,
      toast,
    ],
  );

  const handleDuplicateMerchflow = useCallback(
    (merchflow: ResponseMerchFlowDetails) => {
      openDuplicateMerchflowModal({
        id: merchflow.id,
        name: merchflow.name,
        onSuccess: () => {
          void loadFlows({ ...currentQueryParams, page });
        },
      });
    },
    [currentQueryParams, loadFlows, openDuplicateMerchflowModal, page],
  );

  const getMerchflowMenuOptions = useCallback(
    (merchflow: ResponseMerchFlowDetails) =>
      buildMerchflowRowMenuOptions({
        merchflow,
        canCreate,
        canDelete,
        hideMerchflowActions,
        callbacks: {
          onContinue: continueMerchflow,
          onViewStores: ({ id: merchflowId }) => viewStores(merchflowId),
          onViewTemplates: ({ id: merchflowId }) => viewTemplates(merchflowId),
          onUpdate: handleUpdateFlowReview,
          onViolationReport: ({ id: merchflowId }) => openViolationsReportModal(merchflowId),
          onDuplicate: handleDuplicateMerchflow,
          onDelete: handleDeleteMerchflow,
        },
      }),
    [
      canCreate,
      canDelete,
      continueMerchflow,
      handleDeleteMerchflow,
      handleDuplicateMerchflow,
      handleUpdateFlowReview,
      openViolationsReportModal,
      hideMerchflowActions,
      viewStores,
      viewTemplates,
    ],
  );

  const getMerchflowIndicators = useCallback(
    (merchflow: ResponseMerchFlowDetails) =>
      buildMerchflowInlineIndicators({
        merchflow,
        hideMerchflowActions,
        callbacks: {
          onContinue: continueMerchflow,
          onViewStores: ({ id: merchflowId }) => viewStores(merchflowId),
          onViewTemplates: ({ id: merchflowId }) => viewTemplates(merchflowId),
          onUpdate: handleUpdateFlowReview,
          onViolationReport: ({ id: merchflowId }) => openViolationsReportModal(merchflowId),
          onDuplicate: handleDuplicateMerchflow,
          onDelete: handleDeleteMerchflow,
        },
      }),
    [
      continueMerchflow,
      handleDeleteMerchflow,
      handleDuplicateMerchflow,
      handleUpdateFlowReview,
      openViolationsReportModal,
      hideMerchflowActions,
      viewStores,
      viewTemplates,
    ],
  );

  const handleCellContextMenu = useCallback(
    (event: CellContextMenuEvent<ResponseMerchFlowDetails>) => {
      if (!event.data || !(event.event instanceof MouseEvent)) {
        return;
      }

      openMenu({
        event: event.event,
        menu: {
          options: getMerchflowMenuOptions(event.data),
        },
      });
    },
    [getMerchflowMenuOptions, openMenu],
  );

  const renderNameCell = useCallback(
    (params: ICellRendererParams<ResponseMerchFlowDetails>) => {
      const merchflow = params.data;
      if (!merchflow) {
        return null;
      }

      const indicators = getMerchflowIndicators(merchflow);

      return (
        <Flex width="100%" align="center" justify="between" gap="8px">
          <Flex minWidth="0" overflow="hidden">
            <Text variant="small2" lineClamp={1}>
              {merchflow.name}
            </Text>
          </Flex>

          {indicators.length > 0 && (
            <Flex gap="8px" align="center" flexShrink={0}>
              {indicators.map((indicator) => (
                <Flex
                  key={indicator.kind}
                  className="merchflow-action-icon"
                  onClick={(event: React.MouseEvent<HTMLElement>) => {
                    event.stopPropagation();
                    indicator.onClick();
                  }}
                >
                  <Icon name="alert" color={getMerchflowIndicatorColor(indicator)} size={14} />

                  <Tooltip>{indicator.tooltip}</Tooltip>
                </Flex>
              ))}
            </Flex>
          )}
        </Flex>
      );
    },
    [getMerchflowIndicators],
  );

  const columns = useMemo<ColDef<ResponseMerchFlowDetails>[]>(
    () => [
      {
        field: "id",
        headerName: "ID",
        width: 60,
        headerComponentParams: getSortableHeaderParams("id"),
        filter: BackendInputFilter,
        filterParams: idFilterParams,
      },
      {
        field: "name",
        headerName: "Name",
        minWidth: 150,
        flex: 1.2,
        headerComponentParams: getSortableHeaderParams("name"),
        filter: BackendInputFilter,
        filterParams: nameFilterParams,
        cellRenderer: renderNameCell,
      },
      {
        field: "retailer_category_code",
        headerName: "Category",
        minWidth: 100,
        flex: 1,
        headerComponentParams: getSortableHeaderParams("retailer_category_code"),
        filter: MultiSelectFilter,
        filterParams: {
          options: categoryOptions,
        },
      },
      {
        field: "category_code",
        headerName: "Sub-Category",
        minWidth: 100,
        flex: 1,
        headerComponentParams: getSortableHeaderParams("category_code"),
        filter: MultiSelectFilter,
        filterParams: {
          options: subcategoryOptions,
        },
      },
      {
        field: "go_live_date",
        headerName: "Go-Live Date",
        minWidth: 130,
        headerComponentParams: getSortableHeaderParams("go_live_date"),
        filter: BackendDateRangeFilter,
        valueFormatter: ({ value }: ValueFormatterParams<ResponseMerchFlowDetails, string>) =>
          value ? dateIsoStringToLocaleString(String(value), "DD.MM.YY") : "",
      },
      {
        field: "deactivation_date",
        headerName: "Deactivation Date",
        minWidth: 130,
        headerComponentParams: getSortableHeaderParams("deactivation_date"),
        filter: BackendDateRangeFilter,
        valueFormatter: ({ value }: ValueFormatterParams<ResponseMerchFlowDetails, string>) =>
          value ? dateIsoStringToLocaleString(String(value), "DD.MM.YY") : "",
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 170,
        flex: 1.2,
        headerComponentParams: getSortableHeaderParams("status"),
        filter: MultiSelectFilter,
        filterParams: {
          options: statusFilterOptions,
        },
        valueFormatter: ({ value }: ValueFormatterParams<ResponseMerchFlowDetails, string>) =>
          value ? getMerchflowStatusLabel(value as ResponseMerchFlowDetails["status"]) : "",
      },
      {
        field: "created_at",
        headerName: "Date Created",
        minWidth: 100,
        headerComponentParams: getSortableHeaderParams("created_at"),
        filter: BackendDateRangeFilter,
        valueFormatter: ({ value }: ValueFormatterParams<ResponseMerchFlowDetails, string>) =>
          value ? dateIsoStringToLocaleString(String(value), "DD.MM.YY") : "",
      },
      {
        field: "updated_at",
        headerName: "Date Modified",
        minWidth: 100,
        headerComponentParams: getSortableHeaderParams("updated_at"),
        filter: BackendDateRangeFilter,
        valueFormatter: ({ value }: ValueFormatterParams<ResponseMerchFlowDetails, string>) =>
          value ? dateIsoStringToLocaleString(String(value), "DD.MM.YY") : "",
      },
      {
        field: "notes",
        headerName: "Notes",
        minWidth: 100,
        flex: 1.5,
        cellRenderer: (params: ICellRendererParams<ResponseMerchFlowDetails>) => {
          const merchflow = params.data;
          return merchflow ? (
            <NotesCell
              merchflowId={merchflow.id}
              notes={merchflow.notes}
              setData={setData}
              hideMerchflowActions={hideMerchflowActions}
            />
          ) : null;
        },
      },
      {
        colId: "core_range",
        headerName: "Core range",
        minWidth: 110,
        valueGetter: ({ data }) => data?.config.option || "",
      },
    ],
    [
      categoryOptions,
      getSortableHeaderParams,
      hideMerchflowActions,
      renderNameCell,
      subcategoryOptions,
    ],
  );

  return (
    <Page>
      <MerchflowHeader />

      <Flex column gap="10px" flexGrow>
        {canCreate && !hideMerchflowActions && (
          <Flex justify="right" gap="10px">
            <Button
              color="greenSmoke"
              iconRight={{ name: "plus" }}
              onClick={openMerchflowReviewModal}
            >
              Create
            </Button>
          </Flex>
        )}

        <Flex flexGrow>
          <Scroller>
            <AgGridTable
              columnDefs={columns}
              rowData={data?.data || []}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              onFilterChanged={handleFilterChanged}
              onCellContextMenu={handleCellContextMenu}
              onRowClicked={(event) => {
                const target = event.event?.target as HTMLElement | null;
                if (target?.closest(".merchflow-action-icon")) {
                  return;
                }
                if (event.data) {
                  openMerchflowByDefault(event.data);
                }
              }}
              rowStyle={{ cursor: "pointer" }}
              loading={isLoadingFlows}
              suppressContextMenu
              preventDefaultOnContextMenu
              onColumnMoved={(event) => {
                if (event.finished) {
                  persistGridLayout(event.api);
                }
              }}
              onColumnPinned={(event) => persistGridLayout(event.api)}
              onColumnVisible={(event) => persistGridLayout(event.api)}
              onColumnGroupOpened={(event) => persistGridLayout(event.api)}
              onColumnResized={(event) => {
                if (event.finished) {
                  persistGridLayout(event.api);
                }
              }}
            />
          </Scroller>
        </Flex>

        <Pagination
          page={page}
          setPage={(value) => {
            if (value !== page) {
              setSearchParams({ ...searchParams, page: value });
            }
          }}
          totalPages={data?.totalPages}
          isLoading={data === undefined}
          isUsingInput
        />
      </Flex>
    </Page>
  );
};

const NotesCell = ({
  merchflowId,
  notes,
  setData,
  hideMerchflowActions,
}: {
  merchflowId: number;
  notes: string;
  setData: Dispatch<SetStateAction<ResponseGetMerchflowsReviews | null | undefined>>;
  hideMerchflowActions: boolean;
}) => {
  const refCell = useRef(null);

  const { isHovered } = useHoverClickable(refCell);
  const { openMerchflowNotesModal: _openMerchflowNotesModal } = useMerchflowNotesModal();

  const openMerchflowNotesModal = (event: React.MouseEvent) => {
    event.stopPropagation();
    _openMerchflowNotesModal({
      merchflowId,
      notes,
      onSave: updateMerchflowNotesAfterSuccessfulSave,
    });
  };

  const updateMerchflowNotesAfterSuccessfulSave = (savedNotes: string) => {
    setData((previousData) =>
      previousData
        ? {
            ...previousData,
            data: previousData.data.map((merchflowRow) =>
              merchflowRow.id === merchflowId
                ? { ...merchflowRow, notes: savedNotes }
                : merchflowRow,
            ),
          }
        : null,
    );
  };

  return (
    <Flex
      width="100%"
      height="100%"
      align="center"
      position="relative"
      ref={refCell}
      className={hideMerchflowActions ? undefined : "merchflow-action-icon"}
      onClick={hideMerchflowActions ? undefined : openMerchflowNotesModal}
    >
      <Text variant="small2">{notes}</Text>

      {!hideMerchflowActions && isHovered && (
        <Flex position="absolute" right="0">
          <Icon name="edit" />
        </Flex>
      )}
    </Flex>
  );
};
